import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { ArrowUp, ArrowDown, Filter, Columns, Download, X } from 'react-feather';
import Popover from './Popover';
import { FilasEsqueleto } from './TablaEsqueleto';
import colors from '../../theme/colors';
import FiltroColumna from './FiltroColumna';
import SelectorColumnas from './SelectorColumnas';
import { resumenFiltro } from './filtros';
import './tabla.css';

// Pie de la tabla + margen inferior de la página.
const ALTO_RESERVADO_PIE = 64;
const ANCHO_POR_DEFECTO = 140;
const ANCHO_MINIMO = 60;

// Ancho mínimo de una columna: etiqueta + iconos de orden y filtro, para que la
// cabecera nunca se solape al estrecharla.
const anchoMinimo = (col) => {
  const iconos = (col.sortable ? 18 : 0) + (col.filter ? 22 : 0);
  return Math.max(col.minWidth || ANCHO_MINIMO, Math.ceil(col.label.length * 7.4) + 28 + iconos);
};

const useEsMovil = () => {
  const consulta = '(max-width: 767px)';
  const [movil, setMovil] = useState(() => window.matchMedia?.(consulta)?.matches ?? false);
  useEffect(() => {
    const mq = window.matchMedia?.(consulta);
    if (!mq) return undefined;
    const alCambiar = (e) => setMovil(e.matches);
    mq.addEventListener('change', alCambiar);
    return () => mq.removeEventListener('change', alCambiar);
  }, []);
  return movil;
};

const DENSIDADES = [
  { id: 's', etiqueta: 'S', titulo: 'Compacta' },
  { id: 'm', etiqueta: 'M', titulo: 'Normal' },
  { id: 'l', etiqueta: 'L', titulo: 'Amplia' },
];

const valorVacio = (v) => v === null || v === undefined || v === '';
const SIN_ACCION = () => {};
const CLAVE_POR_DEFECTO = (fila) => `${fila.empresaId}-${fila.id}`;

const TablaEstandar = ({
  tabla,
  filas,
  total = 0,
  cargandoMas = false,
  hayMas = false,
  onCargarMas = SIN_ACCION,
  onFilaClick = undefined,
  loadFacets = undefined,
  onExportar = null,
  exportando = false,
  renderCelda,
  tituloTarjeta,
  nombreEntidad = 'registros',
  claveFila = CLAVE_POR_DEFECTO,
  seleccion = undefined,
  cargando = false,
  sinBotonFiltros = false,
  error = false,
  onReintentar = undefined,
}) => {
  const [filtroAbierto, setFiltroAbierto] = useState(null); // { id, ancla }
  const [columnasAncla, setColumnasAncla] = useState(null);
  const [filtrosAncla, setFiltrosAncla] = useState(null);
  const [anchoVivo, setAnchoVivo] = useState({}); // ancho durante el arrastre
  const redimensionando = useRef(false);
  const contenedorRef = useRef(null);
  const centinelaRef = useRef(null);
  const movil = useEsMovil();

  const visibles = tabla.columnasVisibles;
  // Con selección múltiple, pulsar la fila la marca o desmarca (salvo que la pantalla defina otra cosa).
  const pulsarFila = (fila) => {
    if (onFilaClick) onFilaClick(fila);
    else if (seleccion) seleccion.alternar(fila);
  };
  const nSeleccionadas = seleccion ? filas.filter((f) => seleccion.estaSeleccionada(f)).length : 0;
  const todasMarcadas = !!seleccion && filas.length > 0 && nSeleccionadas === filas.length;
  const anchoDe = (col) =>
    Math.max(
      anchoVivo[col.id] ?? tabla.anchos[col.id] ?? col.width ?? ANCHO_POR_DEFECTO,
      anchoMinimo(col),
    );
  const minTabla = visibles.reduce((suma, col) => suma + anchoDe(col), 0);

  // La zona con scroll ocupa hasta el final de la pantalla (menos el pie): así las barras
  // vertical y horizontal siempre quedan a la vista, sin tener que bajar la página.
  const ajustarAlto = useCallback(() => {
    const nodo = contenedorRef.current;
    if (!nodo || movil) return;
    const alto = window.innerHeight - nodo.getBoundingClientRect().top - ALTO_RESERVADO_PIE;
    nodo.style.maxHeight = `${Math.max(alto, 240)}px`;
  }, [movil]);
  useLayoutEffect(ajustarAlto); // también cuando aparecen o desaparecen los chips de filtros
  useEffect(() => {
    window.addEventListener('resize', ajustarAlto);
    return () => window.removeEventListener('resize', ajustarAlto);
  }, [ajustarAlto]);

  // Scroll infinito: el centinela del final de la lista pide la siguiente tanda.
  useEffect(() => {
    const nodo = centinelaRef.current;
    if (
      !nodo ||
      !hayMas ||
      cargando ||
      cargandoMas ||
      typeof IntersectionObserver === 'undefined'
    ) {
      return undefined;
    }
    const obs = new IntersectionObserver(
      (entradas) => entradas[0].isIntersecting && onCargarMas(),
      { root: movil ? null : contenedorRef.current, rootMargin: '200px' },
    );
    obs.observe(nodo);
    return () => obs.disconnect();
  }, [hayMas, cargando, cargandoMas, onCargarMas, movil, filas.length]);

  const empezarRedimension = useCallback(
    (e, col) => {
      e.preventDefault();
      e.stopPropagation();
      redimensionando.current = true;
      const x0 = e.clientX;
      const w0 = anchoDe(col);
      let ultimo = w0;
      const mover = (ev) => {
        ultimo = Math.max(anchoMinimo(col), w0 + ev.clientX - x0);
        setAnchoVivo((prev) => ({ ...prev, [col.id]: ultimo }));
      };
      const soltar = () => {
        document.removeEventListener('mousemove', mover);
        document.removeEventListener('mouseup', soltar);
        tabla.fijarAncho(col.id, ultimo);
        setAnchoVivo((prev) => {
          const { [col.id]: _quitado, ...resto } = prev;
          return resto;
        });
        // El clic que cierra el arrastre no debe contar como orden.
        setTimeout(() => {
          redimensionando.current = false;
        }, 0);
      };
      document.addEventListener('mousemove', mover);
      document.addEventListener('mouseup', soltar);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tabla],
  );

  const alOrdenar = (id) => {
    if (redimensionando.current) return;
    tabla.ordenar(id);
  };

  const filtroDe = (id) => tabla.filtros[id];
  const colFiltro = filtroAbierto && tabla.columnas.find((c) => c.id === filtroAbierto.id);

  // Acciones queda pegada a la derecha y la primera columna a la izquierda al hacer scroll horizontal.
  const primeraId = visibles.find((c) => !c.fixed)?.id;
  const claseFija = (col) => {
    if (col.fixed) return 'tbl-fija-der';
    return col.id === primeraId ? 'tbl-fija-izq' : undefined;
  };

  const cabecera = (
    <thead>
      <tr>
        {seleccion && (
          <th className="tbl-sel" style={{ width: 40 }}>
            <input
              type="checkbox"
              aria-label="Seleccionar todas las filas"
              checked={todasMarcadas}
              ref={(el) => {
                if (el) el.indeterminate = nSeleccionadas > 0 && !todasMarcadas;
              }}
              onChange={() => seleccion.alternarTodas(filas, !todasMarcadas)}
            />
          </th>
        )}
        {visibles.map((col) => {
          const ordenActivo = tabla.sortBy === col.id;
          const ariaSort = ordenActivo
            ? tabla.sortDir === 'asc'
              ? 'ascending'
              : 'descending'
            : 'none';
          return (
            <th
              key={col.id}
              className={claseFija(col)}
              style={{ width: anchoDe(col) }}
              aria-sort={col.sortable ? ariaSort : undefined}
            >
              <div className={`tbl-th${col.align === 'right' ? ' tbl-th-der' : ''}`}>
                {col.sortable ? (
                  <button
                    type="button"
                    className="tbl-th-etiqueta"
                    onClick={() => alOrdenar(col.id)}
                    title={`Ordenar por ${col.label}`}
                  >
                    <span className="tbl-th-txt">{col.label}</span>
                    <span className={`tbl-orden${ordenActivo ? ' activo' : ''}`} aria-hidden="true">
                      {ordenActivo && tabla.sortDir === 'desc' ? (
                        <ArrowDown size={13} />
                      ) : (
                        <ArrowUp size={13} />
                      )}
                    </span>
                  </button>
                ) : (
                  <span className="tbl-th-etiqueta tbl-th-estatica">
                    <span className="tbl-th-txt">{col.label}</span>
                  </span>
                )}
                {col.filter && (
                  <button
                    type="button"
                    className={`tbl-icono tbl-filtro${filtroDe(col.id) ? ' activo' : ''}`}
                    aria-label={`Filtrar ${col.label}`}
                    aria-pressed={!!filtroDe(col.id)}
                    onClick={(e) => setFiltroAbierto({ id: col.id, ancla: e.currentTarget })}
                  >
                    <Filter size={13} />
                  </button>
                )}
              </div>
              {!col.fixed && (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                <span
                  role="presentation"
                  className="tbl-resize"
                  onMouseDown={(e) => empezarRedimension(e, col)}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
            </th>
          );
        })}
      </tr>
    </thead>
  );

  // Al reordenar o filtrar se sustituyen las filas por relleno hasta que llegan las nuevas.
  const mostrarEsqueleto = cargando;
  const textoRegistros = `${filas.length} de ${total} registros`;

  return (
    <div
      className={`tbl tbl-d-${tabla.density}${seleccion ? ' tbl-con-sel' : ''}${
        cargando ? ' tbl-cargando' : ''
      }`}
      style={{ '--tbl-primario': colors.primary }}
      aria-busy={cargando || undefined}
    >
      <div className="tbl-barra">
        <div className="tbl-barra-titulo">
          <strong>Resultados</strong>
          <span className="tbl-barra-n">{textoRegistros}</span>
          {seleccion && <span className="tbl-barra-n">· {nSeleccionadas} seleccionadas</span>}
        </div>
        <div className="tbl-barra-acciones">
          {onExportar && (
            <button
              type="button"
              className="tbl-btn"
              onClick={onExportar}
              disabled={exportando || total === 0}
            >
              <Download size={13} /> {exportando ? 'Exportando…' : 'Excel'}
            </button>
          )}
          <button
            type="button"
            className="tbl-btn"
            aria-label="Columnas"
            onClick={(e) => setColumnasAncla(columnasAncla ? null : e.currentTarget)}
          >
            <Columns size={13} /> Columnas
          </button>
          {!sinBotonFiltros && tabla.columnas.some((c) => c.filter) && (
            <button
              type="button"
              className="tbl-btn"
              aria-label="Filtros"
              aria-haspopup="true"
              onClick={(e) => setFiltrosAncla(filtrosAncla ? null : e.currentTarget)}
            >
              <Filter size={13} /> Filtros
              {tabla.filtrosActivos.length > 0 && (
                <span className="tbl-btn-n">{tabla.filtrosActivos.length}</span>
              )}
            </button>
          )}
          {movil && (
            <select
              className="tbl-select"
              aria-label="Ordenar por"
              value={tabla.sortBy ? `${tabla.sortBy}:${tabla.sortDir}` : ''}
              onChange={(e) => tabla.fijarOrdenDirecto(e.target.value)}
            >
              <option value="">Sin orden</option>
              {tabla.columnas
                .filter((c) => c.sortable)
                .flatMap((c) => [
                  <option key={`${c.id}:asc`} value={`${c.id}:asc`}>
                    {c.label} ↑
                  </option>,
                  <option key={`${c.id}:desc`} value={`${c.id}:desc`}>
                    {c.label} ↓
                  </option>,
                ])}
            </select>
          )}
          <div className="tbl-densidad" role="group" aria-label="Densidad">
            {DENSIDADES.map((d) => (
              <button
                key={d.id}
                type="button"
                title={d.titulo}
                aria-pressed={tabla.density === d.id}
                className={tabla.density === d.id ? 'activo' : ''}
                onClick={() => tabla.fijarDensidad(d.id)}
              >
                {d.etiqueta}
              </button>
            ))}
          </div>
        </div>
      </div>

      {cargando && <div className="tbl-progreso" role="progressbar" aria-label="Actualizando" />}

      {tabla.filtrosActivos.length > 0 && (
        <div className="tbl-chips" aria-label="Filtros activos">
          <span className="tbl-chips-titulo">Filtros activos</span>
          {tabla.filtrosActivos.map(({ id, label, filtro }) => (
            <span key={id} className="tbl-chip">
              <span>
                {label}: {resumenFiltro(filtro)}
              </span>
              <button
                type="button"
                aria-label={`Quitar filtro ${label}`}
                onClick={() => tabla.fijarFiltro(id, null)}
              >
                <X size={12} />
              </button>
            </span>
          ))}
          <button type="button" className="tbl-link" onClick={tabla.limpiarTodosFiltros}>
            Limpiar filtros
          </button>
        </div>
      )}

      {error ? (
        <div className="tbl-error" role="alert">
          <p>No se han podido cargar los datos.</p>
          {onReintentar && (
            <button type="button" className="tbl-btn" onClick={onReintentar}>
              Reintentar
            </button>
          )}
        </div>
      ) : filas.length === 0 && !mostrarEsqueleto ? (
        <div className="tbl-vacio">
          <p>No hay {nombreEntidad} con los filtros actuales.</p>
          {tabla.filtrosActivos.length > 0 && (
            <button type="button" className="tbl-btn" onClick={tabla.limpiarTodosFiltros}>
              Limpiar filtros
            </button>
          )}
        </div>
      ) : movil ? (
        <ul className="tbl-tarjetas">
          {mostrarEsqueleto &&
            Array.from({ length: 5 }, (_f, i) => (
              <li key={`esq${i}`} className="tbl-tarjeta tbl-esq-tarjeta" aria-hidden="true">
                <span className="tbl-esq tbl-esq-titulo" />
                <span className="tbl-esq" style={{ width: '90%' }} />
                <span className="tbl-esq" style={{ width: '70%' }} />
              </li>
            ))}
          {!mostrarEsqueleto &&
            filas.map((fila) => (
              <li key={claveFila(fila)}>
                <button
                  type="button"
                  className={`tbl-tarjeta${
                    seleccion?.estaSeleccionada(fila) ? ' tbl-tarjeta-sel' : ''
                  }`}
                  aria-pressed={seleccion ? seleccion.estaSeleccionada(fila) : undefined}
                  onClick={() => pulsarFila(fila)}
                >
                  <span className="tbl-tarjeta-titulo">
                    {seleccion?.estaSeleccionada(fila) ? '✓ ' : ''}
                    {tituloTarjeta(fila)}
                  </span>
                  {visibles
                    .filter((c) => !c.fixed)
                    .map((col) => (
                      <span key={col.id} className="tbl-tarjeta-fila">
                        <span className="tbl-tarjeta-etq">{col.label}</span>
                        <span>{valorVacio(fila[col.id]) ? '—' : renderCelda(col, fila)}</span>
                      </span>
                    ))}
                </button>
              </li>
            ))}
          <li ref={centinelaRef} className="tbl-centinela">
            {cargandoMas ? 'Cargando…' : ''}
          </li>
        </ul>
      ) : (
        <div className="tbl-scroll" ref={contenedorRef}>
          <table className="tbl-tabla" style={{ minWidth: minTabla }}>
            {cabecera}
            <tbody>
              {mostrarEsqueleto && (
                <FilasEsqueleto columnas={visibles} conSeleccion={!!seleccion} />
              )}
              {!mostrarEsqueleto &&
                filas.map((fila) => (
                  <tr
                    key={claveFila(fila)}
                    className={seleccion?.estaSeleccionada(fila) ? 'tbl-fila-sel' : undefined}
                    onClick={() => pulsarFila(fila)}
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && pulsarFila(fila)}
                  >
                    {seleccion && (
                      <td className="tbl-sel">
                        <input
                          type="checkbox"
                          aria-label="Seleccionar fila"
                          checked={seleccion.estaSeleccionada(fila)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => seleccion.alternar(fila)}
                        />
                      </td>
                    )}
                    {visibles.map((col) => (
                      <td
                        key={col.id}
                        className={
                          [col.align === 'right' && 'tbl-num', claseFija(col)]
                            .filter(Boolean)
                            .join(' ') || undefined
                        }
                      >
                        {valorVacio(fila[col.id]) && !col.fixed ? (
                          <span className="tbl-nada">—</span>
                        ) : (
                          renderCelda(col, fila)
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              <tr ref={centinelaRef} className="tbl-centinela">
                <td colSpan={visibles.length + (seleccion ? 1 : 0)}>
                  {cargandoMas ? 'Cargando más…' : ''}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <div className="tbl-pie">{textoRegistros}</div>

      {filtroAbierto && colFiltro && (
        <FiltroColumna
          key={filtroAbierto.id}
          columna={colFiltro}
          ancla={filtroAbierto.ancla}
          valorActual={filtroDe(filtroAbierto.id)}
          loadFacets={loadFacets}
          onApply={(valor) => tabla.fijarFiltro(filtroAbierto.id, valor)}
          onClose={() => setFiltroAbierto(null)}
        />
      )}
      {filtrosAncla && (
        <Popover
          ancla={filtrosAncla}
          onClose={() => setFiltrosAncla(null)}
          ancho={240}
          alinear="derecha"
          etiqueta="Filtrar por columna"
        >
          <div className="tbl-pop-titulo">Filtrar por</div>
          <ul className="tbl-cols">
            {tabla.columnas
              .filter((c) => c.filter)
              .map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    className="tbl-opcion tbl-opcion-btn"
                    onClick={() => {
                      setFiltroAbierto({ id: c.id, ancla: filtrosAncla });
                      setFiltrosAncla(null);
                    }}
                  >
                    <span className="tbl-opcion-txt">{c.label}</span>
                    {filtroDe(c.id) && <span className="tbl-opcion-n">activo</span>}
                  </button>
                </li>
              ))}
          </ul>
        </Popover>
      )}
      {columnasAncla && (
        <SelectorColumnas
          ancla={columnasAncla}
          tabla={tabla}
          onClose={() => setColumnasAncla(null)}
        />
      )}
    </div>
  );
};

TablaEstandar.propTypes = {
  tabla: PropTypes.object.isRequired,
  filas: PropTypes.array.isRequired,
  total: PropTypes.number,
  cargandoMas: PropTypes.bool,
  hayMas: PropTypes.bool,
  onCargarMas: PropTypes.func,
  onFilaClick: PropTypes.func,
  loadFacets: PropTypes.func,
  onExportar: PropTypes.func,
  exportando: PropTypes.bool,
  renderCelda: PropTypes.func.isRequired,
  tituloTarjeta: PropTypes.func.isRequired,
  nombreEntidad: PropTypes.string,
  claveFila: PropTypes.func,
  seleccion: PropTypes.shape({
    estaSeleccionada: PropTypes.func.isRequired,
    alternar: PropTypes.func.isRequired,
    alternarTodas: PropTypes.func.isRequired,
  }),
  cargando: PropTypes.bool,
  sinBotonFiltros: PropTypes.bool,
  error: PropTypes.bool,
  onReintentar: PropTypes.func,
};

export default TablaEstandar;
